import { memo, useEffect, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { NotificationManager } from "react-notifications";
import { useSearchParams } from "react-router";

import { useShopSelection } from "shared/components/navigation";
import { InvertedSelectBox, SelectShop } from "./styles";

interface SwitchShopProps {
  disabled: boolean;
}

const SwitchShop = ({ disabled }: SwitchShopProps) => {
  const [searchParams] = useSearchParams();
  const {
    state: { loading, selectedShop, shops },
    actions: { requestShopChange }
  } = useShopSelection();

  const options = useMemo(
    () =>
      shops.map(({ mongoShopId, name, shopId }) => ({
        value: mongoShopId,
        label: `${shopId} - ${name}`
      })),
    [shops]
  );

  const selectedShopId = selectedShop?.shopId;

  useEffect(() => {
    const requestedShopId = searchParams.get("shop");

    if (
      loading ||
      !requestedShopId ||
      selectedShopId === undefined ||
      shops.some(({ shopId }) => shopId === Number(requestedShopId))
    ) {
      return;
    }

    NotificationManager.warning(
      <FormattedMessage
        id="containers.Dashboard.Notification.noShop.description"
        defaultMessage="There is no shop found with id {shopId}, you will be redirected to the first shop in your shop list."
        values={{ shopId: requestedShopId }}
      />,
      <FormattedMessage
        id="containers.Dashboard.Notification.noShop.title"
        defaultMessage="No shop found"
      />,
      9000
    );

  }, [loading, searchParams, selectedShopId, shops]);

  const handleSwitchShop = (mongoShopId: string) => {
    const nextShop = shops.find((shop) => shop.mongoShopId === mongoShopId);

    if (nextShop) {
      requestShopChange(nextShop.shopId);
    }
  };

  if (loading) return null;

  return (
    <SelectShop>
      <span>Switch between shops:</span>
      <InvertedSelectBox
        name="shop"
        onChange={(_name: string, mongoShopId: string) => {
          handleSwitchShop(mongoShopId);
        }}
        value={selectedShop?.mongoShopId}
        options={options}
        disabled={disabled}
        optionsPosition="top"
      />
    </SelectShop>
  );
};

export default memo(SwitchShop);
